import type { PropertyIntelCompsData } from "./usePropertyIntelComps";

const INTEL_COMPS_STORAGE_KEY = "ep_property_intel_comps_v1";
const PROPERTY_DETAIL_STORAGE_KEY = "ep_property_detail_v1";
export const PROPERTY_INTEL_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type PersistedEntry<T> = {
  payload: T;
  cachedAt: number;
};

const pruneStore = <T>(
  store: Record<string, PersistedEntry<T>>,
): Record<string, PersistedEntry<T>> => {
  const now = Date.now();
  const next: Record<string, PersistedEntry<T>> = {};

  for (const [key, entry] of Object.entries(store || {})) {
    if (
      entry?.cachedAt &&
      now - entry.cachedAt < PROPERTY_INTEL_CACHE_TTL_MS &&
      entry.payload != null
    ) {
      next[key] = entry;
    }
  }

  return next;
};

const readStore = <T>(storageKey: string): Record<string, PersistedEntry<T>> => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }

    const store = pruneStore<T>(JSON.parse(raw) as Record<string, PersistedEntry<T>>);
    window.localStorage.setItem(storageKey, JSON.stringify(store));
    return store;
  } catch {
    return {};
  }
};

const writeStore = <T>(
  storageKey: string,
  store: Record<string, PersistedEntry<T>>,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(pruneStore(store)));
  } catch {
    // Ignore quota or privacy mode failures.
  }
};

export function readPersistedIntelComps(
  cacheKey: string,
): PropertyIntelCompsData | null {
  const store = readStore<PropertyIntelCompsData>(INTEL_COMPS_STORAGE_KEY);
  return store[cacheKey]?.payload ?? null;
}

export function writePersistedIntelComps(
  cacheKey: string,
  payload: PropertyIntelCompsData,
): void {
  const store = readStore<PropertyIntelCompsData>(INTEL_COMPS_STORAGE_KEY);
  store[cacheKey] = { payload, cachedAt: Date.now() };
  writeStore(INTEL_COMPS_STORAGE_KEY, store);
}

export function clearPersistedIntelComps(cacheKey: string): void {
  const store = readStore<PropertyIntelCompsData>(INTEL_COMPS_STORAGE_KEY);
  delete store[cacheKey];
  writeStore(INTEL_COMPS_STORAGE_KEY, store);
}

export function buildPropertyDetailCacheKey(
  apiPrefix: string,
  detailId: string | number,
): string {
  return `${apiPrefix}:${detailId}`;
}

export type PropertyDetailCacheResponse = {
  success?: boolean;
  data?: Record<string, unknown>;
  message?: string;
};

export function readPersistedPropertyDetail(
  apiPrefix: string,
  detailId: string | number,
): PropertyDetailCacheResponse | null {
  const cacheKey = buildPropertyDetailCacheKey(apiPrefix, detailId);
  const store = readStore<PropertyDetailCacheResponse>(PROPERTY_DETAIL_STORAGE_KEY);
  return store[cacheKey]?.payload ?? null;
}

export function writePersistedPropertyDetail(
  apiPrefix: string,
  detailId: string | number,
  response: PropertyDetailCacheResponse,
): void {
  if (response?.success !== true) {
    return;
  }

  const cacheKey = buildPropertyDetailCacheKey(apiPrefix, detailId);
  const store = readStore<PropertyDetailCacheResponse>(PROPERTY_DETAIL_STORAGE_KEY);
  store[cacheKey] = { payload: response, cachedAt: Date.now() };
  writeStore(PROPERTY_DETAIL_STORAGE_KEY, store);
}

export function clearPersistedPropertyDetail(
  apiPrefix: string,
  detailId: string | number,
): void {
  const cacheKey = buildPropertyDetailCacheKey(apiPrefix, detailId);
  const store = readStore<PropertyDetailCacheResponse>(PROPERTY_DETAIL_STORAGE_KEY);
  delete store[cacheKey];
  writeStore(PROPERTY_DETAIL_STORAGE_KEY, store);
}

export function parsePropertyDetailCacheContext(url?: string): {
  apiPrefix: string;
  detailId: string | null;
} {
  if (!url) {
    return { apiPrefix: "customer", detailId: null };
  }

  const match = url.match(/([^/?]+)\/property-detail\/([^/?]+)/);
  if (!match) {
    return { apiPrefix: "customer", detailId: null };
  }

  return {
    apiPrefix: match[1] || "customer",
    detailId: match[2] || null,
  };
}
